import {HttpClient} from "@angular/common/http";
import {forkJoin, Observable, of, switchMap} from "rxjs";
import {catchError, map} from "rxjs/operators";
import _ from "lodash";
import {CoreEnvironmentService} from "@core/services/environment/core-environment.service";
import {TranslateLoader} from "@ngx-translate/core";

export class CoreCustomTranslateHttpLoaderService implements TranslateLoader {

  private _envCustomTranslationPath: string = '';
  private _warnings: string [] = [];

  constructor(protected _http: HttpClient) {
  }

  getTranslation(lang: string): Observable<any> {
    const env = CoreEnvironmentService.getEnvironment();
    const envLangName: string | undefined = env?.lang?.name;
    const hasEnvLangName: boolean = typeof envLangName === 'string' && envLangName.trim() !== '';
    this._envCustomTranslationPath = `./assets/i18n/custom/${ lang }-${ envLangName }.json`;
    this._warnings = [];

    const baseTranslation$ = this._http.get(`./assets/i18n/${ lang }.json`).pipe(
      catchError(() => of({}))
    );
    const customTranslation$ = this._http.get(this._envCustomTranslationPath).pipe(
      catchError(() => {
        console.error(`[i18n] Failed to load custom translation from: ${ this._envCustomTranslationPath }`);
        return of({});
      })
    )


    if (!hasEnvLangName) {
      return baseTranslation$;
    }

    return this._isTranslationPathExists().pipe(
      switchMap((exists: boolean) => {
        if (!exists) {
          console.warn(`[i18n] Custom translation not found at: ${ this._envCustomTranslationPath }`);
          return baseTranslation$;
        }

        return forkJoin([baseTranslation$, customTranslation$]).pipe(
          map(([base, custom]) => this._mergeTranslations(base, custom))
        );
      }),
      catchError(() => {
        return baseTranslation$;
      })
    );
  }

  private _isTranslationPathExists(): Observable<boolean> {
    return this._http.head(this._envCustomTranslationPath).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  private _mergeTranslations(base: Object, custom: Object): Object {
    const merged = _.mergeWith({}, base, custom);

    this._compareObjects(custom, base, []);

    if (this._warnings.length) {
      console.warn(
        `[i18n] Custom translation "${ this._envCustomTranslationPath }" keys not found in base: \n` + this._warnings.join('\n')
      );
    }

    return merged;
  }

  private _compareObjects(customObj: any, baseObj: any, path: string[]): void {
    if (baseObj === undefined) {
      this._warnings.push(`Key -> "${ path.join('.') }".`);
      return;
    }

    if (_.isObject(customObj)) {
      _.forOwn(customObj, (value, key) => {
        this._compareObjects(value, baseObj[key], [...path, key]);
      });
    }
  }
}
